// @ts-check
// =============================================================================
// js/app/quickplay.js — the deckbiter.cards/quickplay onboarding mode.
//
// WHAT THIS IS
//   Served at GET /quickplay (src/http/pages.js), the SAME client bundle as
//   "/" — this module is what the path changes. The normal boot (loading,
//   then the 18+ gate on first visit) runs untouched; the difference starts
//   the moment the session is ready: instead of landing in the lobby, the
//   player is dropped STRAIGHT into a bot duel — John vs John, the starter
//   matchup — with a one-time welcome popup covering only the absolute
//   basics (data/guide-content.js QUICKPLAY_GUIDE_HTML).
//
// FLOW (boot.js hands over at session-ready)
//   1. splash stays on stage saying "Starting your game" (splash.js checks
//      isQuickplay()) while this module picks John's prebuilt deck, waits for
//      the bot scripts, and requests "Bot Game" against the Human Bot.
//   2. The returned duel lands in the store; boot's duel watcher points the
//      hash at #/duel, and boot starts the router which renders it.
//   3. The duel screen calls maybeShowQuickplayIntro() on mount — the basics
//      popup opens over the board, once per browser tab.
//   On ANY failure the player falls through to the normal lobby with a toast
//   instead of being stranded on the splash.
// =============================================================================

import { store, setSlice } from '../core/store.js';
import { request } from '../net/http.js';
import { loadBots } from '../net/bot-runner.js';
import { selectDeck } from '../ui/deck-list.js';
import { openModal } from '../ui/modal.js';
import { toast } from '../ui/toast.js';
import { callStatsAPI } from './analytics.js';
import { QUICKPLAY_GUIDE_HTML } from '../data/guide-content.js';

// The mode check lives in its own leaf module (session.js also needs it);
// re-exported here so mode consumers have one import site.
import { isQuickplay } from './quickplay-mode.js';
export { isQuickplay };

/** The starter matchup: John, Just A Boring Human (hero 0) on both sides. */
const JOHN_HERO_ID = 0;
const JOHN_BOT_ID = 'BoringHuman_auri';

/** sessionStorage flag: the welcome popup shows once per tab, not per game. */
const INTRO_SEEN_KEY = 'quickplayIntroSeen';

/** In-memory backstop for the popup when sessionStorage is refused. */
let introShown = false;

/**
 * Start the John vs John bot duel. Called by boot.js once the session is
 * ready (guest or logged-in — prebuilt decks resolve for everyone). On
 * success the duel slice is set, which is all boot needs to route into the
 * game. Mirrors challenge-ladder.js startDuel minus the pickers.
 * @returns {Promise<boolean>}  true when the duel is live in the store.
 */
export async function startQuickplayDuel() {
	// John's prebuilt deck (the server ships defaultdecks with the login).
	const prebuilts = store.userData?.defaultdecks || [];
	const deck = prebuilts.find((d) => d && Number(d.hero) === JOHN_HERO_ID) || prebuilts[0];
	if (!deck?.list) {
		toast('No starter deck available. Pick one in the lobby!', { error: true });
		return false;
	}
	selectDeck(deck, deck.index);

	// The bot scripts self-register after loading — wait for John's brain.
	await loadBots();
	const bot = (/** @type {any} */ (window).biterbots || {})[JOHN_BOT_ID];
	if (!bot) {
		toast('The bot failed to load. Try the Duel Bot screen!', { error: true });
		return false;
	}

	callStatsAPI('Duel:Quickplay');
	const returned = await request('Bot Game', {
		deckIndex: deck.index,
		botIndex: bot.deckIndex,
		botName: JOHN_BOT_ID,
		tier: 0,
	});
	if (!returned || returned.message === 'Fail' || !returned.duel) {
		toast(returned?.reason || 'Could not start the game.', { error: true });
		return false;
	}
	if (returned.duels) {
		setSlice('lobby', { ...store.lobby, duels: returned.duels });
	}
	setSlice('duel', returned.duel); // boot's duel watcher takes it from here
	return true;
}

/**
 * Show the quickplay basics popup, once per tab. The duel screen calls this
 * on every mount; it no-ops outside quickplay mode, for spectators, and after
 * the first showing (so reconnects and rematches don't re-pop it).
 * @param {any} duel  The mounted duel (from store.duel).
 * @returns {void}
 */
export function maybeShowQuickplayIntro(duel) {
	if (!isQuickplay()) return;
	if (!duel || duel.spectating || duel.GameOver) return;
	if (introShown) return;
	introShown = true;
	try {
		if (sessionStorage.getItem(INTRO_SEEN_KEY)) return;
		sessionStorage.setItem(INTRO_SEEN_KEY, 'true');
	} catch {
		// storage refused (private mode) — the in-memory flag above still
		// keeps it to one showing per page load
	}
	const box = openModal({
		title: 'How To Play',
		body: `<div class="guide-popup">${QUICKPLAY_GUIDE_HTML}</div>`,
		buttons: [{ label: "Let's Play" }],
	});
	// This sheet reads better wide: 50% over the standard modal cap.
	box.classList.add('modal-box--wide');
}
